import { getDelayedStore } from '../utils/delayedStore';
import { ReportContext, ReportStore } from './ReportContext';

// Map from element key to the most recent ReportStoreManager for that key
const reportStores: Map<string, ReportStoreManager> = new Map();

// Set of element keys that have been marked for extended lifetime
const retainedKeys: Set<string> = new Set();

export function forEachStore(cb: (value: ReportStoreManager, key: string) => void) {
  reportStores.forEach((val, key) => cb(val, key));
}

export function hasExtendedLifetime(elementKey: string) {
  return retainedKeys.has(elementKey);
}

export function extendStoreLifetime(elementKey: string) {
  if (retainedKeys.has(elementKey)) {
    console.warn(`Store with key ${elementKey} has already been marked for extended lifetime`);
    return;
  }
  retainedKeys.add(elementKey);

  const storeManager = reportStores.get(elementKey);
  if (storeManager) {
    storeManager.retain();
  }
}

export function releaseStoreLifetime(elementKey: string) {
  if (!retainedKeys.has(elementKey)) {
    console.warn(`Store with key ${elementKey} is not marked for extended lifetime`);
    return;
  }
  retainedKeys.delete(elementKey);

  const storeManager = reportStores.get(elementKey);
  if (storeManager) {
    storeManager.release();
  }
}

export function getReportStoreManager(elementKey: string) {
  return reportStores.get(elementKey);
}

export function getOrCreateReportStoreManager(
  reportContext: ReportContext,
  elementKey: string,
  objectName?: string | number
): ReportStoreManager {
  let storeManager = reportStores.get(elementKey);
  if (
    storeManager &&
    (storeManager.context !== reportContext || storeManager.objectName !== objectName)
  ) {
    // A change in context or objectName means the old store should be discarded
    if (retainedKeys.has(elementKey)) {
      storeManager.release();
    }
    reportStores.delete(elementKey);
    storeManager = undefined;
  }
  if (!storeManager) {
    storeManager = new ReportStoreManager(reportContext, elementKey, objectName);
    if (retainedKeys.has(elementKey)) {
      storeManager.retain();
    }
    reportStores.set(elementKey, storeManager);
  }
  return storeManager;
}

export class ReportStoreManager {
  private _store: {
    store: ReportStore;
    delayedStore: ReportStore;
  };
  private _context: ReportContext;
  private _elementKey: string;
  private _objectName: string | number | undefined;
  private _retainCount: number;

  constructor(context: ReportContext, elementKey: string, objectName: string | number | undefined) {
    this._store = getDelayedStore(context.initializeStore(objectName));
    this._context = context.retain();
    this._elementKey = elementKey;
    this._objectName = objectName;
    this._retainCount = 0;
  }

  get store() {
    return this._store.delayedStore;
  }

  get context() {
    return this._context;
  }

  get objectName() {
    return this._objectName;
  }

  retain() {
    this._retainCount++;
  }

  release() {
    if (this._retainCount <= 0) {
      throw new Error('ReportContext released without being retained');
    }
    this._retainCount--;
    if (this._retainCount === 0) {
      this.destroy();
    }
  }

  private destroy() {
    this._context.destroyStore(this._store.store);
    if (reportStores.get(this._elementKey) === this) {
      reportStores.delete(this._elementKey);
    }
    this._context.release();
  }
}
